/**
 * Data loading for a single widget.
 *
 * Every widget fetches independently so one unavailable service degrades one
 * tile instead of the dashboard. The hook owns the loading / error / retry
 * lifecycle and the optional refresh timer.
 *
 * `cacheKey` is a serialised description of everything the request depends on
 * (source id, field, statistic, filters…). Passing the dependencies as a string
 * rather than an array keeps the loader closure free to change on every render
 * without re-triggering the request.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type WidgetStatus = 'loading' | 'ready' | 'error';

export interface WidgetDataState<T> {
  data: T | null;
  status: WidgetStatus;
  error: string | null;
  /** Re-runs the request; bound to the Retry button. */
  reload: () => void;
  /** When the current data was loaded, for the "updated at" footer. */
  loadedAt: number | null;
}

export function useWidgetData<T>(
  loader: () => Promise<T>,
  cacheKey: string,
  refreshInterval = 0,
  /** Skips fetching while the widget is not fully configured. */
  enabled = true,
): WidgetDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<WidgetStatus>(enabled ? 'loading' : 'ready');
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Held in a ref so a new closure each render does not restart the request.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!enabled) {
      setStatus('ready');
      setData(null);
      setError(null);
      return undefined;
    }

    let active = true;
    setStatus('loading');
    setError(null);

    loaderRef
      .current()
      .then((result) => {
        if (!active) return;
        setData(result);
        setStatus('ready');
        setLoadedAt(Date.now());
      })
      .catch((caught: unknown) => {
        if (!active) return;
        console.error('[dashboard] widget data failed', caught);
        setError(caught instanceof Error ? caught.message : 'Unable to load data');
        setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [cacheKey, reloadToken, enabled]);

  // Automatic refresh is opt-in; the default (0) never polls.
  useEffect(() => {
    if (!enabled || !refreshInterval) return undefined;
    const timer = window.setInterval(reload, refreshInterval);
    return () => window.clearInterval(timer);
  }, [refreshInterval, reload, enabled]);

  return { data, status, error, reload, loadedAt };
}
